import { z } from 'zod';
import { apiClient } from '../client';

/**
 * The client portal's own settings (W5, ENG-19, `GET|PATCH /api/portal/settings`).
 * Viewers only. The profile is the viewer's user account; the notification
 * preferences are those of its customer's link (null when it has none). The
 * CRM and Calendly configuration is the agency's and is not writable here
 * (DUP-1, DUP-2): `agency_name` says who to ask.
 */

export const NOTIFICATION_PREF_KEYS = [
  'weekly_report_email', 'weekly_report_app',
  'monthly_goal_email', 'monthly_goal_app',
  'new_meeting_email', 'new_meeting_app',
  'new_prospect_email', 'new_prospect_app',
] as const;

export type NotificationPrefKey = (typeof NOTIFICATION_PREF_KEYS)[number];
export type NotificationPrefs = Record<NotificationPrefKey, boolean>;

const PrefsSchema = z.object(
  Object.fromEntries(NOTIFICATION_PREF_KEYS.map((k) => [k, z.boolean().default(true)])) as Record<NotificationPrefKey, z.ZodDefault<z.ZodBoolean>>,
);

const PortalSettingsSchema = z.object({
  profile: z.object({
    full_name: z.string().default(''),
    email: z.string().default(''),
    company_name: z.string().default(''),
  }),
  notification_prefs: PrefsSchema.nullable(),
  agency_name: z.string().default(''),
});

export type PortalSettings = z.infer<typeof PortalSettingsSchema>;

export interface PortalSettingsPatch {
  full_name?: string;
  notification_prefs?: Partial<NotificationPrefs>;
}

export const PORTAL_SETTINGS_QUERY_KEY = ['portal-settings'] as const;

export async function getPortalSettings(opts?: { signal?: AbortSignal }): Promise<PortalSettings> {
  return PortalSettingsSchema.parse(await apiClient.get<unknown>('/portal/settings', opts));
}

export async function updatePortalSettings(patch: PortalSettingsPatch): Promise<PortalSettings> {
  return PortalSettingsSchema.parse(await apiClient.patch<unknown>('/portal/settings', patch));
}
